import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { SubentityState } from 'app/projects/subentity/src/lib/models/subentity.state';
import {
    CreatePerformancePeriodSuccess,
    DeselectSelectedPerformancePeriods,
    FetchPerformancePeriods,
    FetchPerformancePeriodsFailure,
    FetchPerformancePeriodsSuccess,
    SearchPerformancePeriods,
    SelectFilteredPerformancePeriods,
    SetPerformancePeriods,
    ToggleSelectedPerformancePeriod,
} from '../actions';
import { PerformancePeriodsStateModel } from './performance-periods.state-model';
import { PerformancePeriodState } from './performance-period.state';
import { PerformancePeriod } from './performance-period.model';
import { AddPerformancePeriods } from '../actions/add-entities.action';
import { PerformancePeriodApiService } from '../services/performance-period.api-service';
import { map } from 'rxjs/operators';

@State<PerformancePeriodsStateModel>({
    name: 'performancePeriods',
    defaults: new PerformancePeriodsStateModel(),
})
@Injectable()
export class PerformancePeriodsState extends SubentityState<PerformancePeriod> {
    @Selector([PerformancePeriodState.getEntities])
    static getFilteredEntities(state: PerformancePeriodsStateModel, performancePeriods: PerformancePeriod[]): PerformancePeriod[] {
        if (!performancePeriods) {
            return null;
        }

        return performancePeriods
            .filter((item) => state.filtered.indexOf(item.id) !== -1)
            .map((item) => {
                return [state.list.indexOf(item.id), item];
            })
            .sort((a: [number, PerformancePeriod], b: [number, PerformancePeriod]) => {
                return a[0] > b[0] ? 1 : b[0] > a[0] ? -1 : 0;
            })
            .map((x: [number, PerformancePeriod]) => x[1]);
    }

    @Selector()
    static getSelectedEntityIds(state: PerformancePeriodsStateModel): string[] {
        return state.selected;
    }

    @Selector()
    static getSearchTerm(state: PerformancePeriodsStateModel): string {
        return state.searchTerm;
    }

    @Selector()
    static getTotalCount(state: PerformancePeriodsStateModel): number {
        return state.totalCount;
    }

    @Selector()
    static isFetchingList(state: PerformancePeriodsStateModel): boolean {
        return state.fetchingList;
    }

    constructor(private _performancePeriodApiService: PerformancePeriodApiService, private store: Store) {
        super(null, 0);

        this._propstoSearch = [...PerformancePeriod.props_to_search];
    }

    @Action(AddPerformancePeriods)
    addEntities(ctx: StateContext<PerformancePeriodsStateModel>, action: AddPerformancePeriods): void {
        const performancePeriods = this.store.selectSnapshot(PerformancePeriodState.getEntities) || [];

        this._patchSubentities(ctx, { payload: { subentityIds: action.payload.map((entity) => entity.id) } }, performancePeriods);
    }

    @Action(CreatePerformancePeriodSuccess)
    createEntity(ctx: StateContext<PerformancePeriodsStateModel>, action: CreatePerformancePeriodSuccess): void {
        const performancePeriods = this.store.selectSnapshot(PerformancePeriodState.getEntities) || [];

        this._patchSubentities(ctx, { payload: { subentityIds: [action.payload.id] } }, performancePeriods);
    }

    @Action(SetPerformancePeriods)
    setEntities(ctx: StateContext<PerformancePeriodsStateModel>, action: SetPerformancePeriods): void {
        this._setSubentities(ctx, action, null);
    }

    @Action(ToggleSelectedPerformancePeriod)
    toggleSelect(ctx: StateContext<PerformancePeriodsStateModel>, action: ToggleSelectedPerformancePeriod): void {
        this._toggleSelectedSubentity(ctx, action);
    }

    @Action(SelectFilteredPerformancePeriods)
    selectAll(ctx: StateContext<PerformancePeriodsStateModel>): void {
        this._selectFilteredSubentities(ctx);
    }

    @Action(DeselectSelectedPerformancePeriods)
    deselectAll(ctx: StateContext<PerformancePeriodsStateModel>): void {
        this._deselectSelectedSubentities(ctx);
    }

    @Action(SearchPerformancePeriods)
    search(ctx: StateContext<PerformancePeriodsStateModel>, action: SearchPerformancePeriods): void {
        const performancePeriods = this.store.selectSnapshot(PerformancePeriodState.getEntities);

        this._searchSubentities(ctx, action, performancePeriods);
    }

    @Action(FetchPerformancePeriods)
    fetchPerformancePeriods(ctx: StateContext<PerformancePeriodsStateModel>, action: FetchPerformancePeriods): void {
        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetPerformancePeriods,
            FetchPerformancePeriodsSuccess,
            FetchPerformancePeriodsFailure,
            this._performancePeriodApiService.getAllPerformancePeriods(action.payload?.pageSize, action.payload?.pageNumber, action.payload?.sortBy).pipe(
                map((query) => {
                    ctx.patchState({
                        totalCount: query.pagination.number_of_results,
                    });

                    return query.data.map((query) => new PerformancePeriod(query));
                })
            )
        );
    }

    @Action(FetchPerformancePeriodsSuccess)
    fetchAllSuccess(ctx: StateContext<PerformancePeriodsStateModel>): void {
        this._fetchSubentitiesSuccess(ctx);
    }

    @Action(FetchPerformancePeriodsFailure)
    fetchAllFailure(ctx: StateContext<PerformancePeriodsStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
    }
}
